import { z } from 'zod';
/**
 * Platoons and teams share one collection with a `type` discriminator and a
 * `parentId` (REQUIREMENTS §2.3), so a deeper level can be added later without
 * a schema migration. Nothing in the code may assume exactly two levels.
 */
export const unitTypeSchema = z.enum(['platoon', 'team']);
export const unitSchema = z.object({
    id: z.string().min(1),
    type: unitTypeSchema,
    name: z.string(),
    /** null for a top-level unit. */
    parentId: z.string().nullable(),
    commanderUids: z.array(z.string()),
    order: z.number().int(),
    active: z.boolean(),
});
/**
 * GET /api/units — the whole org structure, flat.
 *
 * Flat rather than nested on purpose: a recursive response schema is awkward to
 * validate and the tree is cheap to assemble on the client, where it is also
 * the only place that cares about nesting.
 */
export const unitsResponseSchema = z.object({
    units: z.array(unitSchema),
    /**
     * The subset of `units` whose roster this caller may read. Lets the UI hide
     * links it would only get a 403 from — a convenience, never the control.
     * The server re-derives it on every roster request.
     */
    viewableUnitIds: z.array(z.string()),
});
/**
 * Builds the org tree from the flat list. A unit whose parent is missing or
 * inactive is surfaced at the root rather than silently dropped — an orphaned
 * team is a data problem someone needs to see, not one to hide.
 */
export function buildUnitTree(units) {
    const byId = new Map(units.map((u) => [u.id, { ...u, children: [] }]));
    const roots = [];
    for (const node of byId.values()) {
        const parent = node.parentId === null ? undefined : byId.get(node.parentId);
        if (parent) {
            parent.children.push(node);
        }
        else {
            roots.push(node);
        }
    }
    const byOrder = (a, b) => a.order - b.order || a.name.localeCompare(b.name);
    const sortDeep = (nodes) => {
        nodes.sort(byOrder);
        nodes.forEach((n) => sortDeep(n.children));
        return nodes;
    };
    return sortDeep(roots);
}
/**
 * POST /api/units — create a platoon or a team. Admin only (§2.2: the
 * csm "cannot change roles or org structure").
 *
 * A team names its parent; a platoon has none. Enforced by the refinement
 * rather than by two request shapes, so the same form serves both.
 */
export const createUnitRequestSchema = z
    .object({
    type: unitTypeSchema,
    name: z.string().trim().min(1).max(80),
    parentId: z.string().min(1).nullable(),
    commanderUids: z.array(z.string().min(1)).max(10).default([]),
})
    .refine((u) => (u.type === 'team') === (u.parentId !== null), {
    message: 'A team must name its parent platoon; a platoon must not',
    path: ['parentId'],
});
/**
 * PATCH /api/units/:unitId — rename, reorder, (de)activate, or change who
 * commands it. `type` and `parentId` are deliberately not editable: moving a
 * team between platoons would silently change the chain of command over every
 * soldier in it, and the denormalised `platoonId` on each of them. That is a
 * migration, not a patch.
 *
 * There is no DELETE. Soldiers, assignments and ledger entries reference unit
 * ids forever; a unit that is no longer in use is deactivated and kept.
 */
export const updateUnitRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    order: z.number().int().min(0).max(10_000),
    active: z.boolean(),
    commanderUids: z.array(z.string().min(1)).max(10),
})
    .partial()
    .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be supplied',
});
export const unitResponseSchema = z.object({ unit: unitSchema });
//# sourceMappingURL=units.js.map